import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parse, isWithinInterval, startOfYear, endOfYear, subDays, isValid } from 'date-fns';
import {
    Search,
    ChevronLeft,
    ChevronRight,
    FileText,
    Eye,
    Filter,
    X,
    ChevronDown,
    Check,
    Calendar as CalendarIcon
} from 'lucide-react';
import apiService from '../config';

const RecentPublications = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [activePreset, setActivePreset] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const dropdownRef = useRef(null);
    const itemsPerPage = 8;
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [docModalOpen, setDocModalOpen] = useState(false);
    const [docUrl, setDocUrl] = useState('');

    const openDocModal = (url) => {
        if (!url) return;
        setDocUrl(url);
        setDocModalOpen(true);
    };

    const closeDocModal = () => {
        setDocModalOpen(false);
        setDocUrl('');
    };

    useEffect(() => {
        fetchRecentPublications();
    }, []);

    const fetchRecentPublications = async () => {
        setLoading(true);
        try {
            const res = await apiService.getRecentPublications();

            if (res.status === 200) {
                // normalize backend data → UI-friendly data
                const formatted = (res.data.data || res.data || []).map(item => ({
                    id: item.publication_id,
                    title: item.case_no,
                    description: item.description,
                    startDate: formatToDDMMYYYY(item.start_date),
                    endDate: formatToDDMMYYYY(item.end_date),
                    file: item.document
                }));

                setRecords(formatted);
            }
        } catch (err) {
            console.error('Failed to fetch publications', err);
        } finally {
            setLoading(false);
        }
    };

    const formatToDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset to page 1 when search or filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, dateRange.start, dateRange.end]);

    const handlePresetChange = (preset) => {
        setActivePreset(preset);
        const today = new Date();
        let start = '';
        let end = '';

        if (preset === 'last30') {
            const priorDate = subDays(today, 30);
            start = format(priorDate, 'yyyy-MM-dd');
            end = format(today, 'yyyy-MM-dd');
        } else if (preset === 'thisYear') {
            start = format(startOfYear(today), 'yyyy-MM-dd');
            end = format(endOfYear(today), 'yyyy-MM-dd');
        } else if (preset === 'lastYear') {
            const lastYear = subDays(startOfYear(today), 1);
            start = format(startOfYear(lastYear), 'yyyy-MM-dd');
            end = format(endOfYear(lastYear), 'yyyy-MM-dd');
        } else if (preset === 'all') {
            start = '';
            end = '';
        }

        setDateRange({ start, end });
        if (preset !== 'custom') {
            setShowFilterDropdown(false);
        }
    };

    const handleCustomDateChange = (type, value) => {
        setActivePreset('custom');
        setDateRange(prev => ({ ...prev, [type]: value }));
    };

    // Robust Date Parsing Helper
    const parseRecordDate = (dateStr) => {
        if (!dateStr) return null;
        let parsedDate = parse(dateStr, 'dd/MM/yyyy', new Date());
        if (isValid(parsedDate)) return parsedDate;

        parsedDate = new Date(dateStr);
        if (isValid(parsedDate)) return parsedDate;

        return null;
    };

    // Filter records
    const filteredRecords = useMemo(() => {
        return records.filter(record => {
            const matchesSearch =
                record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.description.toLowerCase().includes(searchTerm.toLowerCase());

            let matchesDate = true;
            if (dateRange.start || dateRange.end) {
                const recordStart = parseRecordDate(record.startDate);

                if (recordStart) {
                    const filterStart = dateRange.start ? new Date(dateRange.start) : null;
                    const filterEnd = dateRange.end ? new Date(dateRange.end) : null;

                    if (filterStart) filterStart.setHours(0, 0, 0, 0);
                    if (filterEnd) filterEnd.setHours(23, 59, 59, 999);
                    recordStart.setHours(12, 0, 0, 0);

                    if (filterStart && recordStart < filterStart) matchesDate = false;
                    if (filterEnd && recordStart > filterEnd) matchesDate = false;
                }
            }

            return matchesSearch && matchesDate;
        });
    }, [records, searchTerm, dateRange]);

    const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
    const currentRecords = filteredRecords.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-300 font-sans relative overflow-hidden w-full">

            {/* Header */}
            <div className="py-5 border-b border-gray-200 bg-white">

                <div className="px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white">
                    <div className="flex items-center gap-3 mb-4 sm:mb-0">
                        <div className="p-2.5 bg-yellow-500 rounded-lg shadow-md shadow-yellow-500/20">
                            <FileText className="text-white w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 tracking-tight">Notification/Publications</h2>
                            <p className="text-xs text-gray-500 font-medium">Official documents & notices</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                        {/* Search */}
                        <div className="relative group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-yellow-600 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="pl-9 pr-8 py-2 w-full sm:w-64 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all outline-none placeholder-gray-400 text-gray-700"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>

                        {/* Filter Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all shadow-sm ${activePreset !== 'all' || dateRange.start
                                    ? 'bg-yellow-500 border-yellow-500 text-white'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                <Filter className="w-4 h-4" />
                                <span className="hidden sm:inline">Filter</span>
                                <ChevronDown className={`w-3 h-3 transition-transform ${showFilterDropdown ? 'rotate-180' : ''}`} />
                                {(activePreset !== 'all' || dateRange.start) &&
                                    <span className={`w-2 h-2 rounded-full ml-1 ${activePreset !== 'all' || dateRange.start ? 'bg-white' : 'bg-yellow-500'}`}></span>
                                }
                            </button>

                            <AnimatePresence>
                                {showFilterDropdown && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 5, scale: 0.98 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 5, scale: 0.98 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 ring-1 ring-black/5 z-50 overflow-hidden"
                                        style={{ position: 'absolute', zIndex: 100 }}
                                    >
                                        <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Date Filters</span>
                                            <button onClick={() => handlePresetChange('all')} className="text-[10px] font-medium text-gray-400 hover:text-gray-600">Reset</button>
                                        </div>
                                        <div className="p-1 space-y-0.5">
                                            {[
                                                { id: 'all', label: 'All Time' },
                                                { id: 'last30', label: 'Last 30 Days' },
                                                { id: 'thisYear', label: 'This Year' },
                                                { id: 'lastYear', label: 'Last Year' }
                                            ].map((preset) => (
                                                <button
                                                    key={preset.id}
                                                    onClick={() => handlePresetChange(preset.id)}
                                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${activePreset === preset.id
                                                        ? 'bg-yellow-100 text-yellow-800 font-medium'
                                                        : 'text-gray-600 hover:bg-yellow-50'
                                                        }`}
                                                >
                                                    {preset.label}
                                                    {activePreset === preset.id && <Check className="w-3.5 h-3.5" />}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="p-3 border-t border-gray-200 bg-gray-50/30">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">Custom Range</span>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <span className="w-8 text-[10px] uppercase font-bold text-gray-400">From</span>
                                                    <input
                                                        type="date"
                                                        className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none text-xs"
                                                        value={dateRange.start}
                                                        onChange={(e) => handleCustomDateChange('start', e.target.value)}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <span className="w-8 text-[10px] uppercase font-bold text-gray-400">To</span>
                                                    <input
                                                        type="date"
                                                        className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none text-xs"
                                                        value={dateRange.end}
                                                        onChange={(e) => handleCustomDateChange('end', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Content */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left min-w-full">
                    <thead>
                        <tr className="bg-yellow-500 text-white">
                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider border-r border-yellow-400/50 w-16">Sr.</th>
                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider border-r border-yellow-400/50">Case No</th>
                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider border-r border-yellow-400/50">Description</th>
                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-32 border-r border-yellow-400/50">Start</th>
                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-32 border-r border-yellow-400/50">End</th>
                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center w-24">File</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        <AnimatePresence mode="wait">
                            {currentRecords.length > 0 ? (
                                currentRecords.map((record, index) => (
                                    <motion.tr
                                        key={record.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ delay: index * 0.03 }}
                                        className="group hover:bg-yellow-50 transition-colors"
                                    >
                                        <td className="px-6 py-4 border-r border-gray-200 text-center">
                                            <span className="text-gray-500 text-sm font-mono group-hover:text-yellow-700 font-medium">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 border-r border-gray-200">
                                            <span className="text-sm font-bold text-gray-800 group-hover:text-gray-900">{record.title}</span>
                                        </td>
                                        <td className="px-6 py-4 border-r border-gray-200">
                                            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed" title={record.description}>
                                                {record.description}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 border-r border-gray-200 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="text-sm font-medium text-gray-600">{record.startDate}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-r border-gray-200 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="text-sm font-medium text-gray-600">{record.endDate}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {record.file ? (
                                                <button
                                                    onClick={() => openDocModal(record.file)}
                                                    className="text-gray-600 hover:text-white hover:bg-yellow-500 px-3 py-1.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md text-xs font-semibold"
                                                >
                                                    View
                                                </button>
                                            ) : (
                                                <span className="text-gray-300">-</span>
                                            )}
                                        </td>
                                    </motion.tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center border-b border-gray-200">
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            <Search className="w-10 h-10 opacity-20 mb-3" />
                                            <p className="text-base font-medium text-gray-500">No records found</p>
                                            <button onClick={() => { setSearchTerm(''); handlePresetChange('all'); }} className="mt-2 text-sm font-semibold text-yellow-600 hover:text-yellow-700 hover:underline transition-all">
                                                Clear Filters
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </AnimatePresence>
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between rounded-b-xl">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Page <span className="text-gray-900">{currentPage}</span> of {Math.max(1, totalPages)}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <div className="flex gap-1">
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => handlePageChange(i + 1)}
                                className={`w-7 h-7 text-xs font-bold rounded-lg transition-all border ${currentPage === i + 1
                                    ? 'bg-yellow-500 text-white border-yellow-500 shadow-md transform -translate-y-0.5'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
            {docModalOpen && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-5xl h-[90vh] rounded-xl shadow-2xl flex flex-col">

                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-3 bg-yellow-500 text-white rounded-t-xl">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5" />
                                <h3 className="font-bold text-lg">Document Preview</h3>
                            </div>
                            <button
                                onClick={closeDocModal}
                                className="hover:bg-yellow-600 p-1.5 rounded-lg transition"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 bg-gray-100">
                            <iframe
                                src={docUrl}
                                title="Document Viewer"
                                className="w-full h-full border-0"
                            />
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default RecentPublications;
